import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Smartphone, ExternalLink, Loader2 } from 'lucide-react';
import { useCheckout } from '../lib/useCheckout';

const NAV = [
  { label: 'Overview', href: '/' },
  { label: 'What You Get', href: '/#what-you-get' },
  { label: 'SELF OS', href: '/#selfos' },
  { label: 'Role Model AI', href: '/#role-model-ai' },
  { label: 'Earn', href: '/#essence' },
  { label: 'Security', href: '/#security' },
  { label: 'Specs', href: '/#specs' },
];

export function SiteShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const { openCheckout, loading: checkoutLoading } = useCheckout();

  return (
    <div className="relative min-h-screen bg-black font-sans text-white">
      <div className="phone-grain fixed inset-0 z-0" aria-hidden />

      <header className="relative z-40 border-b border-white/[0.06] bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="group flex items-center gap-2.5 font-display text-lg font-bold tracking-tight text-white sm:text-xl">
            <span className="relative flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500/20 to-cyan-500/10 ring-1 ring-sky-400/30 transition-all duration-300 group-hover:ring-sky-400/60 group-hover:shadow-[0_0_12px_rgba(56,189,248,0.2)]">
              <Smartphone className="h-4 w-4 text-sky-400" />
            </span>
            <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
              SELF Phone
            </span>
          </Link>

          <nav className="hidden items-center gap-0.5 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-1.5 py-1 md:flex">
            {NAV.map((item) => (
              item.href === '/'
                ? (
                    <Link
                      key={item.href}
                      to="/"
                      className="relative rounded-xl px-3.5 py-2 text-sm font-medium text-white/50 transition-all duration-300 hover:text-white/80"
                    >
                      {item.label}
                    </Link>
                  )
                : (
                    <a
                      key={item.href}
                      href={item.href}
                      className="relative rounded-xl px-3.5 py-2 text-sm font-medium text-white/50 transition-all duration-300 hover:text-white/80"
                    >
                      {item.label}
                    </a>
                  )
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openCheckout}
              disabled={checkoutLoading}
              className="hidden items-center gap-1.5 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 px-4 py-2 text-sm font-semibold text-black transition-all duration-300 hover:from-sky-400 hover:to-cyan-400 hover:shadow-[0_0_16px_rgba(56,189,248,0.35)] sm:inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkoutLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  Buy
                  <ExternalLink className="h-3.5 w-3.5" />
                </>
              )}
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-white/60 transition-all duration-200 hover:border-white/20 hover:bg-white/[0.06] hover:text-white md:hidden"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open ? (
          <nav className="border-t border-white/[0.06] bg-black/95 backdrop-blur-xl px-4 py-4 md:hidden">
            <div className="flex flex-col gap-1">
              {NAV.map((item) => (
                item.href === '/'
                  ? (
                      <Link
                        key={item.href}
                        to="/"
                        onClick={() => setOpen(false)}
                        className="relative rounded-xl px-4 py-3 text-sm font-medium text-white/60 transition-all duration-200 hover:bg-white/[0.04] hover:text-white"
                      >
                        {item.label}
                      </Link>
                    )
                  : (
                      <a
                        key={item.href}
                        href={item.href}
                        onClick={() => setOpen(false)}
                        className="relative rounded-xl px-4 py-3 text-sm font-medium text-white/60 transition-all duration-200 hover:bg-white/[0.04] hover:text-white"
                      >
                        {item.label}
                      </a>
                    )
              ))}
            </div>
            <button
              type="button"
              onClick={() => { openCheckout(); setOpen(false); }}
              disabled={checkoutLoading}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 px-4 py-3 text-sm font-semibold text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkoutLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Buy SELF Phone
                  <ExternalLink className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </nav>
        ) : null}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-white/[0.06]">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="flex items-center gap-2 font-display text-xl font-bold text-white">
                <Smartphone className="h-5 w-5 text-sky-400" />
                SELF Phone
              </p>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-white/50">
                SELF Phone is the Resonatia mesh node in your pocket — download models, sync to the
                network, and earn Essence when API requests are processed through your device.
              </p>
            </div>
            <div className="flex flex-col gap-2 text-sm text-white/50">
              <button
                type="button"
                onClick={openCheckout}
                disabled={checkoutLoading}
                className="text-left font-semibold text-sky-400 hover:text-sky-300 disabled:opacity-50"
              >
                {checkoutLoading ? 'Opening...' : 'Buy SELF Phone'}
              </button>
              <a href="https://selflabs.xyz" className="hover:text-white">
                selflabs.xyz
              </a>
              <a
                href="https://rolemodel.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                Privacy
              </a>
              <a
                href="https://rolemodel.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
              >
                Terms
              </a>
            </div>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-white/40">
            SELF Phone is a product of SELF Labs Inc. SELF OS beta is a prototype sideload for Android;
            compute may use battery when opted in. ESSENCE is a stable-value token for rewards and agent
            payments — not a regulated stablecoin, not FDIC-insured. Resonatia mesh node functionality
            requires model sync and network connectivity. Satellite connectivity depends on provider
            coverage and may vary by region.
          </p>
        </div>
      </footer>
    </div>
  );
}